/**
 * pdfText — thin wrapper over pdfjs-dist for client-side text-layer extraction.
 *
 * GameChanger PDFs embed a real text layer (verified against live exports), so we
 * extract positioned text items and reconstruct structure from x/y — no OCR.
 *
 * Works in the browser and in the Capacitor iOS webview (the worker is bundled by
 * Vite via the `?url` import). Pure infrastructure: the scorebook / stats parsers
 * sit on top of `loadPdfPages`. Shared types live in ./types (Node-safe).
 */

import * as pdfjsLib from 'pdfjs-dist';
// Vite resolves this to a hashed URL at build time; bundled into dist for native.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { PdfPage, PdfTextItem, PdfInput } from './types';

export type { PdfPage, PdfTextItem, PdfInput } from './types';
export { groupIntoRows } from './types';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

/** Normalize any supported input into the bytes pdfjs wants. A string is treated
 *  as base64 (with or without a `data:...;base64,` prefix). */
async function toBytes(input: PdfInput): Promise<Uint8Array> {
  if (typeof input === 'string') {
    const base64 = input.includes('base64,') ? input.split('base64,')[1] : input;
    const binary = atob(base64.trim());
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  if (input instanceof Uint8Array) return input;
  if (input instanceof ArrayBuffer) return new Uint8Array(input);
  return new Uint8Array(await input.arrayBuffer()); // File | Blob
}

/**
 * Extract every page's positioned text. Returns one PdfPage per page; items carry
 * top-down y so the parsers can reconstruct the grid by x/y.
 */
export async function loadPdfPages(input: PdfInput): Promise<PdfPage[]> {
  const data = await toBytes(input);
  const doc = await pdfjsLib.getDocument({ data: data.slice() }).promise; // slice: pdfjs detaches the buffer

  try {
    const pages: PdfPage[] = [];
    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      const page = await doc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1 });
      const content = await page.getTextContent();

      const items: PdfTextItem[] = [];
      for (const raw of content.items) {
        if (!('str' in raw)) continue; // skip TextMarkedContent
        const t = raw as { str: string; transform: number[]; width: number; height: number; fontName?: string };
        if (t.str === '') continue;
        items.push({
          str: t.str,
          x: t.transform[4],
          y: viewport.height - t.transform[5], // flip to top-down
          width: t.width,
          height: t.height,
          fontName: t.fontName,
        });
      }

      pages.push({ pageNumber, width: viewport.width, height: viewport.height, items });
      page.cleanup();
    }
    return pages;
  } finally {
    await doc.destroy();
  }
}
